import type { LatLngBounds } from "leaflet";
import L from "leaflet";

import { formatCountdownForContext, getHeatMapColor } from "./countdown.utils";

import type { ProcessedEvent, GeocodedEvent } from "@/types/calendar";

// Cache for geocoded addresses to avoid repeated API calls
const geocodeCache = new Map<string, [number, number]>();

/**
 * Sleep utility for rate limiting
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Geocode an address using OpenStreetMap's Nominatim service with retry logic
 * Free, no API key required
 * @param address - The address to geocode
 * @param retryCount - Current retry attempt (for exponential backoff)
 * @returns Coordinates [lat, lon] or null if geocoding fails
 */
export const geocodeAddress = async (
  address: string,
  retryCount = 0,
): Promise<[number, number] | null> => {
  const MAX_RETRIES = 3;
  const BASE_DELAY = 1000; // 1 second base delay (Nominatim requirement)

  // Check cache first
  if (geocodeCache.has(address)) {
    console.log("✓ Geocoding cache hit for:", address);
    return geocodeCache.get(address)!;
  }

  console.log(
    `🔍 Geocoding address (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`,
    address,
  );

  try {
    // Respect Nominatim usage policy: 1 request per second
    if (retryCount === 0) {
      await sleep(BASE_DELAY);
    } else {
      // Exponential backoff for retries: 2s, 4s, 8s
      const backoffDelay = BASE_DELAY * Math.pow(2, retryCount);
      console.log(`⏳ Waiting ${backoffDelay}ms before retry...`);
      await sleep(backoffDelay);
    }

    // Parse address components for better geocoding
    const venuePattern = /^([^,]+),\s*(.+)$/;
    const match = address.match(venuePattern);
    let baseAddress = address;

    // Remove venue name if present
    if (match && !/\d/.test(match[1])) {
      baseAddress = match[2].trim();
      console.log("📍 Detected venue name, will try without it");
    }

    // Remove suite/apartment/unit numbers (common geocoding failure point)
    const suitePatterns = [
      /,?\s*(suite|ste|unit|apt|apartment|#)\s*[a-z0-9]+/gi,
      /,?\s*#\s*[a-z0-9]+/gi,
    ];

    let addressWithoutSuite = baseAddress;
    for (const pattern of suitePatterns) {
      addressWithoutSuite = addressWithoutSuite.replace(pattern, "");
    }
    addressWithoutSuite = addressWithoutSuite.trim();

    // Extract just the street address (before city)
    const streetAddressPattern = /^(\d+\s+[^,]+?)(?:,\s*(.+))?$/;
    const streetMatch = addressWithoutSuite.match(streetAddressPattern);
    let cityStateZip = "";

    if (streetMatch && streetMatch[2]) {
      cityStateZip = streetMatch[2].trim();
    }

    // Build geocoding strategies from most specific to least specific
    const strategies = [];

    // Strategy 1: Full original address
    strategies.push({ label: "Full address", address: address });

    // Strategy 2: Without venue name
    if (baseAddress !== address) {
      strategies.push({ label: "Without venue name", address: baseAddress });
    }

    // Strategy 3: Without suite/apt numbers (MOST IMPORTANT for addresses like yours!)
    if (addressWithoutSuite !== baseAddress) {
      strategies.push({
        label: "Without suite number",
        address: addressWithoutSuite,
      });
      console.log("🏢 Will try without suite/apt number:", addressWithoutSuite);
    }

    // Strategy 4: Just city, state, zip (for general area)
    if (cityStateZip) {
      strategies.push({ label: "City/State/Zip only", address: cityStateZip });
    }

    // Strategy 5: Add USA if not present
    if (
      !address.toLowerCase().includes("usa") &&
      !address.toLowerCase().includes("united states")
    ) {
      strategies.push({
        label: "With USA suffix",
        address: `${addressWithoutSuite}, USA`,
      });
    }

    console.log(
      `📋 Generated ${strategies.length} geocoding strategies to try`,
    );

    for (const strategy of strategies) {
      console.log(`🎯 Trying strategy: ${strategy.label}`);

      const encodedAddress = encodeURIComponent(strategy.address);
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1&addressdetails=1`;

      const response = await fetch(url, {
        headers: {
          "User-Agent": "AIG-Calendar-App/1.0", // Nominatim requires User-Agent
        },
      });

      if (!response.ok) {
        console.error(
          `❌ Geocoding request failed: ${response.status} ${response.statusText}`,
        );

        // Handle rate limiting specifically
        if (response.status === 429) {
          console.warn("⚠️ Rate limit hit, will retry with backoff");
          if (retryCount < MAX_RETRIES) {
            return geocodeAddress(address, retryCount + 1);
          }
          throw new Error("Rate limit exceeded");
        }

        continue; // Try next strategy
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        const coordinates: [number, number] = [lat, lon];

        console.log(
          `✅ Geocoded successfully (${strategy.label}):`,
          address,
          "->",
          coordinates,
        );
        console.log("   Found location:", data[0].display_name);

        // Cache the result using the ORIGINAL address as key
        geocodeCache.set(address, coordinates);

        // Persist to localStorage
        try {
          const storedCache = localStorage.getItem("geocodeCache") || "{}";
          const cache = JSON.parse(storedCache);
          cache[address] = coordinates;
          localStorage.setItem("geocodeCache", JSON.stringify(cache));
        } catch (e) {
          console.warn("Failed to persist geocode cache:", e);
        }

        return coordinates;
      }

      console.log(`ℹ️ No results for strategy: ${strategy.label}`);
    }

    // If all strategies failed and we haven't exceeded retries
    if (retryCount < MAX_RETRIES) {
      console.log("🔄 All strategies failed, retrying...");
      return geocodeAddress(address, retryCount + 1);
    }

    console.warn("❌ No geocoding results found after all attempts:", address);
    return null;
  } catch (error) {
    console.error("❌ Geocoding error for address:", address, error);

    // Retry on network errors
    if (retryCount < MAX_RETRIES) {
      console.log("🔄 Retrying due to error...");
      return geocodeAddress(address, retryCount + 1);
    }

    return null;
  }
};

/**
 * Load geocode cache from localStorage on initialization
 */
export const loadGeocodeCache = (): void => {
  try {
    const storedCache = localStorage.getItem("geocodeCache");
    if (storedCache) {
      const cache = JSON.parse(storedCache);
      Object.entries(cache).forEach(([address, coords]) => {
        geocodeCache.set(address, coords as [number, number]);
      });
    }
  } catch (e) {
    console.warn("Failed to load geocode cache:", e);
  }
};

/**
 * Get marker color based on event countdown using heat map scale
 * Provides smooth visual feedback of time remaining
 */
export const getMarkerColor = (event: GeocodedEvent): string => {
  // If event has countdown data, use heat map colors for better visual feedback
  if (event.countdownData) {
    const { color } = getHeatMapColor(event.countdownData.timeRemaining);
    return color;
  }

  // Fallback to category-based colors for events without countdown
  if (event.title?.toLowerCase().includes("meeting")) {
    return "#7c3aed"; // Purple (primary company color)
  }
  if (event.title?.toLowerCase().includes("deadline")) {
    return "#dc2626"; // Red
  }
  if (event.title?.toLowerCase().includes("workshop")) {
    return "#60a5fa"; // Blue
  }
  if (event.title?.toLowerCase().includes("personal")) {
    return "#a78bfa"; // Light purple
  }

  // Default to company primary color
  return "#9b87f5";
};

/**
 * Get marker border color using heat map scale
 */
export const getMarkerBorderColor = (event: GeocodedEvent): string => {
  if (event.countdownData) {
    const { borderColor } = getHeatMapColor(event.countdownData.timeRemaining);
    return borderColor;
  }
  return "#ffffff";
};

/**
 * Group events by location for handling multiple events at the same address
 */
export const groupEventsByLocation = (
  events: GeocodedEvent[],
): Map<string, GeocodedEvent[]> => {
  const grouped = new Map<string, GeocodedEvent[]>();

  events.forEach((event) => {
    if (event.location) {
      const key = event.location.toLowerCase().trim();
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(event);
    }
  });

  return grouped;
};

/**
 * Calculate optimal map bounds to show all events
 */
export const calculateMapBounds = (
  events: GeocodedEvent[],
): LatLngBounds | null => {
  const eventsWithCoords = events.filter((e) => e.coordinates);

  if (eventsWithCoords.length === 0) {
    return null;
  }

  const bounds = L.latLngBounds(eventsWithCoords.map((e) => e.coordinates!));

  return bounds;
};

/**
 * Get a user-friendly time range label
 */
export const getTimeFilterLabel = (
  filter: "today" | "week" | "month",
): string => {
  switch (filter) {
    case "today":
      return "Today";
    case "week":
      return "This Week";
    case "month":
      return "This Month";
    default:
      return "This Week";
  }
};

/**
 * Filter events by time range
 */
export const filterEventsByTimeRange = (
  events: ProcessedEvent[],
  filter: "today" | "week" | "month",
): ProcessedEvent[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case "today": {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return events.filter((e) => e.start >= today && e.start < tomorrow);
    }

    case "week": {
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      return events.filter((e) => e.start >= today && e.start < weekFromNow);
    }

    case "month": {
      const monthFromNow = new Date(today);
      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
      return events.filter((e) => e.start >= today && e.start < monthFromNow);
    }

    default:
      return events;
  }
};

/**
 * Create a popup content for a map marker with countdown display
 */
export const createMarkerPopupContent = (events: GeocodedEvent[]): string => {
  if (events.length === 1) {
    const event = events[0];
    const targetDate = event.nextOccurrence || event.start;
    const timeStr = targetDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    const countdown = event.countdownData;
    const urgency = countdown?.urgencyLevel || "far";

    return `
      <div class="map-event-popup">
        <div class="popup-header" style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
          <h3 class="font-semibold text-gray-900" style="flex: 1;">${event.title}</h3>
          ${event.isRecurring ? '<span class="recurring-badge" style="font-size: 10px; padding: 2px 8px; background: #e0e7ff; color: #4f46e5; border-radius: 12px; font-weight: 600; white-space: nowrap; margin-left: 8px;">🔁 Recurring</span>' : ""}
        </div>

        ${
          countdown
            ? `
          <div class="countdown-display countdown-${urgency}" style="
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 16px;
            padding: 16px;
            border-radius: 12px;
            margin-top: 12px;
            background: ${
              urgency === "imminent"
                ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                : urgency === "now"
                  ? "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            };
            color: white;
            ${urgency === "imminent" || urgency === "now" ? "animation: pulse 2s infinite;" : ""}
          ">
            <div style="font-size: 32px;">⏱️</div>
            <div style="display: flex; flex-direction: column; align-items: flex-start;">
              <span style="font-size: 10px; opacity: 0.95; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
                ${countdown.isHappening ? "HAPPENING NOW" : countdown.isPast ? "ENDED" : "STARTS IN"}
              </span>
              <span style="
                font-size: 28px;
                font-weight: 700;
                font-family: 'Courier New', monospace;
                letter-spacing: 1px;
                text-shadow: 0 2px 4px rgba(0,0,0,0.3);
              ">
                ${formatCountdownForContext(countdown, "popup")}
              </span>
            </div>
          </div>
        `
            : ""
        }

        <p class="text-sm text-gray-600 mt-2">
          ${
            event.nextOccurrence
              ? `Next: ${event.nextOccurrence.toLocaleDateString()} at ${timeStr}`
              : `${event.start.toLocaleDateString()} at ${timeStr}`
          }
        </p>

        ${event.description ? `<p class="text-sm text-gray-500 mt-2">${event.description}</p>` : ""}

        <button class="popup-action-btn" style="
          width: 100%;
          margin-top: 12px;
          padding: 8px;
          background: #4f46e5;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        ">
          View Details →
        </button>
      </div>

      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .popup-action-btn:hover {
          background: #4338ca;
        }
      </style>
    `;
  } else {
    // Multiple events at this location
    return `
      <div class="map-event-popup">
        <h3 class="font-semibold text-gray-900">${events.length} events at this location</h3>
        <ul class="mt-2 space-y-1">
          ${events
            .slice(0, 3)
            .map((event) => {
              const targetDate = event.nextOccurrence || event.start;
              const timeStr = targetDate.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              });
              const countdown = event.countdownData
                ? formatCountdownForContext(event.countdownData, "list")
                : "";
              return `
              <li class="text-sm" style="padding: 6px; border-bottom: 1px solid #e5e7eb;">
                <div style="font-weight: 600;">${event.title}</div>
                <div style="display: flex; justify-content: space-between; margin-top: 2px;">
                  <span style="color: #6b7280;">${timeStr}</span>
                  ${countdown ? `<span style="color: #4f46e5; font-size: 12px;">${countdown}</span>` : ""}
                </div>
              </li>
            `;
            })
            .join("")}
          ${events.length > 3 ? `<li class="text-sm text-gray-500" style="padding: 6px;">...and ${events.length - 3} more</li>` : ""}
        </ul>
        <button class="popup-action-btn" style="
          width: 100%;
          margin-top: 12px;
          padding: 8px;
          background: #4f46e5;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        ">
          View All →
        </button>
      </div>
    `;
  }
};

// Initialize cache on module load
loadGeocodeCache();
